import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import FractionInput from '../components/FractionInput';
import { formatFraction } from '../utils/fractions';
import { formatCurrency } from '../utils/currency';

const TABS = ['Quotes', 'Inquiries'];

const INQUIRY_STAGES = [
  { value: 'new_inquiry', label: 'New Inquiry' },
  { value: 'qualifying', label: 'Qualifying' },
  { value: 'quote_sent', label: 'Quote Sent' },
  { value: 'following_up', label: 'Following Up' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const EMPTY_OPP = {
  customer_id: '', title: '', stage: 'new_inquiry', estimated_value: '', expected_close: '', notes: ''
};

const THICKNESS_OPTS = ['1/8"', '3/16"', '1/4"', '3/8"', '1/2"'];

const DEFAULT_IGU = {
  oa_size: '', outboard_lite: 'Clear Tempered', outboard_thickness: '1/4"', air_space: '1/2"', air_type: 'AIR',
  inboard_lite: 'Clear W/ Low E', inboard_thickness: '1/4"', low_e: 'yes', low_e_type: 'Hardcoat', low_e_surface: 'Side 2',
  primary_seal: 'PIB', secondary_seal: 'Butyl', edge_bite: '1/2"',
  lead_type: 'Classic 1/2" Oval Profile', lead_sides: '1 & 4',
  leaded_units: 'yes', perimeter_lead: 'no', soldered_cos: 'yes', logos: 'No Logos (Certified)',
  grids_in_units: 'yes', muntin_colour: 'Dark Bronze', muntin_size: '1/2" x 3/8"',
  spacer_bar_colour: 'D. Bronze', spacer_bar_size: 'Standard',
  surface_finish: 'Natural', warranty: '10 Years',
  notes: ''
};

const MUNTIN_SIZES = ['1/2" x 3/16"', '1/2" x 3/8"', '5/8" x 3/16"', '5/8" x 3/8"', '3/4" x 1/4"', '3/4" x 3/8"'];

function normalizeSpec(raw) {
  const out = { ...DEFAULT_IGU, ...raw };
  ['low_e', 'leaded_units', 'grids_in_units', 'soldered_cos', 'perimeter_lead'].forEach(k => {
    if (typeof out[k] === 'boolean') out[k] = out[k] ? 'yes' : 'no';
  });
  return out;
}

const EMPTY_LINE = {
  location_description: '', width_inches: '', height_inches: '', quantity: 1,
  glass_shape: 'RECT', pattern_type: 'SQ', grids_across: 0, grids_down: 0,
  is_frosted: false, unit_price: '', unit_cost: '', notes: ''
};

function sqFt(w, h) {
  const wn = parseFloat(w), hn = parseFloat(h);
  if (!wn || !hn) return null;
  return (wn * hn / 144).toFixed(3);
}

// ============================================================
// New Quote Modal
// ============================================================
function NewQuoteModal({ customers, onClose, onSaved }) {
  const [form, setForm] = useState({ quote_number: '', customer_id: '', status: 'draft', expiry_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const save = async () => {
    if (!form.quote_number.trim()) return setError('Quote number is required');
    if (!form.customer_id) return setError('Customer is required');
    setSaving(true);
    try {
      const res = await api.post('/quotes', {
        quote_number: form.quote_number,
        customer_id: parseInt(form.customer_id),
        status: form.status,
        expiry_date: form.expiry_date || null,
        notes: form.notes,
      });
      onSaved(res.data.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">New Quote</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-grid">
            <div className="form-group">
              <label>Quote Number *</label>
              <input value={form.quote_number} onChange={e => set('quote_number', e.target.value)} placeholder="e.g. Q-2026-001" />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Customer *</label>
            <select value={form.customer_id} onChange={e => set('customer_id', e.target.value)}>
              <option value="">Select customer...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Expiry Date</label>
            <input type="date" value={form.expiry_date || ''} onChange={e => set('expiry_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Create & Open'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Opportunity Modal
// ============================================================
function OppModal({ opp, customers, onClose, onSaved }) {
  const [form, setForm] = useState(opp || EMPTY_OPP);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!opp;
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const save = async () => {
    if (!form.customer_id) return setError('Customer is required');
    if (!form.title) return setError('Title is required');
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id ? parseInt(form.customer_id) : null,
        title: form.title, stage: form.stage,
        estimated_value: form.estimated_value !== '' ? parseFloat(form.estimated_value) : null,
        expected_close: form.expected_close || null, notes: form.notes,
      };
      if (isEdit) await api.patch(`/opportunities/${opp.id}`, payload);
      else await api.post('/opportunities', payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Inquiry' : 'New Inquiry'}</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label>Customer *</label>
            <select value={form.customer_id} onChange={e => set('customer_id', e.target.value)}>
              <option value="">Select customer...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Shopfront glazing — High St" />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Stage</label>
              <select value={form.stage} onChange={e => set('stage', e.target.value)}>
                {INQUIRY_STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Estimated Value ($)</label>
              <input type="number" step="0.01" value={form.estimated_value || ''} onChange={e => set('estimated_value', e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>Expected Close</label>
              <input type="date" value={form.expected_close || ''} onChange={e => set('expected_close', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Inquiry'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// IGU Spec Block
// ============================================================
const secLbl = { fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, marginTop: 4 };
const miniInput = { fontSize: 12, padding: '4px 8px' };
const miniGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: '8px 12px', marginBottom: 16 };
const fgMin = { margin: 0 };

function IguSpecBlock({ quoteId, initial, onUpdated }) {
  const [spec, setSpec] = useState(() => normalizeSpec(initial));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(false);
  const set = (k, v) => setSpec(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/quotes/${quoteId}`, { igu_spec: spec });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onUpdated?.();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const tf = (label, key) => (
    <div key={key} className="form-group" style={fgMin}>
      <label style={{ fontSize: 11 }}>{label}</label>
      <input value={spec[key] || ''} onChange={e => set(key, e.target.value)} style={miniInput} />
    </div>
  );

  const sf = (label, key, opts) => (
    <div key={key} className="form-group" style={fgMin}>
      <label style={{ fontSize: 11 }}>{label}</label>
      <select value={spec[key] || ''} onChange={e => set(key, e.target.value)} style={miniInput}>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ background: 'var(--bg-card, var(--bg-surface))', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 16 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', cursor: 'pointer', borderBottom: open ? '1px solid var(--border)' : 'none' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
          IGU Specification
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{open ? '▲ collapse' : '▼ expand'}</span>
      </div>
      {open && (
        <div style={{ padding: 16 }}>
          <div style={secLbl}>1 — Glass Stack</div>
          <div style={miniGrid}>
            {tf('O/A Size', 'oa_size')}
            {tf('Outboard Lite', 'outboard_lite')}
            {sf('Outboard Thickness', 'outboard_thickness', THICKNESS_OPTS)}
            {tf('Air Space', 'air_space')}
            {sf('Air Type', 'air_type', ['AIR', 'ARGON', 'KRYPTON'])}
            {tf('Inboard Lite', 'inboard_lite')}
            {sf('Inboard Thickness', 'inboard_thickness', THICKNESS_OPTS)}
            {sf('Low-E', 'low_e', ['yes', 'no'])}
            {spec.low_e === 'yes' && tf('Low-E Type', 'low_e_type')}
            {spec.low_e === 'yes' && sf('Low-E Surface', 'low_e_surface', ['Side 2', 'Side 3', 'Side 4'])}
            {tf('Primary Seal', 'primary_seal')}
            {tf('Secondary Seal', 'secondary_seal')}
            {tf('Edge Bite', 'edge_bite')}
          </div>

          <div style={secLbl}>2 — Lead</div>
          <div style={miniGrid}>
            {tf('Lead Type', 'lead_type')}
            {tf('Lead Sides', 'lead_sides')}
            {sf('Leaded Units', 'leaded_units', ['yes', 'no'])}
            {sf('Perimeter Lead', 'perimeter_lead', ['yes', 'no'])}
            {sf("Soldered C/O's", 'soldered_cos', ['yes', 'no'])}
            {tf('Logos', 'logos')}
          </div>

          <div style={secLbl}>3 — Grids / Internal Muntins</div>
          <div style={miniGrid}>
            {sf('Grids in Units', 'grids_in_units', ['yes', 'no'])}
            {spec.grids_in_units === 'yes' && sf('Muntin Colour', 'muntin_colour', ['Black', 'Dark Bronze', 'Silver'])}
            {spec.grids_in_units === 'yes' && sf('Muntin Size', 'muntin_size', MUNTIN_SIZES)}
          </div>

          <div style={secLbl}>4 — Spacer</div>
          <div style={miniGrid}>
            {tf('Spacer Bar Colour', 'spacer_bar_colour')}
            {tf('Spacer Bar Size', 'spacer_bar_size')}
          </div>

          <div style={secLbl}>5 — Finish</div>
          <div style={miniGrid}>
            {tf('Surface Finish', 'surface_finish')}
            {tf('Warranty', 'warranty')}
          </div>

          <div style={secLbl}>6 — Notes</div>
          <div style={{ marginBottom: 16 }}>
            <textarea value={spec.notes || ''} onChange={e => set('notes', e.target.value)} rows={2}
              style={{ fontSize: 12, padding: '6px 8px', width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Spec'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Add Line Item Form
// ============================================================
function AddLineRow({ quoteId, onAdded }) {
  const [form, setForm] = useState({ ...EMPTY_LINE });
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const sf = sqFt(form.width_inches, form.height_inches);

  const add = async () => {
    setSaving(true);
    try {
      await api.post(`/quotes/${quoteId}/lines`, {
        location_description: form.location_description, description: form.location_description,
        width_inches: form.width_inches != null ? Number(form.width_inches) : null,
        height_inches: form.height_inches != null ? Number(form.height_inches) : null,
        quantity: parseInt(form.quantity) || 1, glass_shape: form.glass_shape,
        pattern_type: form.pattern_type, grids_across: parseInt(form.grids_across) || 0,
        grids_down: parseInt(form.grids_down) || 0, is_frosted: form.is_frosted,
        unit_price: form.unit_price !== '' ? parseFloat(form.unit_price) : null,
        unit_cost: form.unit_cost !== '' ? parseFloat(form.unit_cost) : null,
        notes: form.notes || null,
      });
      setForm({ ...EMPTY_LINE });
      onAdded();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const inp = { fontSize: 12, padding: '3px 6px' };
  return (
    <>
      <tr style={{ background: 'rgba(255,160,0,0.04)' }}>
        <td><input value={form.location_description} onChange={e => set('location_description', e.target.value)} placeholder="Location..." style={{ ...inp, width: '100%', minWidth: 110 }} /></td>
        <td>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <FractionInput value={form.width_inches} onChange={v => set('width_inches', v)} placeholder='W"' style={{ ...inp, width: 72 }} />
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>×</span>
            <FractionInput value={form.height_inches} onChange={v => set('height_inches', v)} placeholder='H"' style={{ ...inp, width: 72 }} />
          </div>
        </td>
        <td><input type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)} style={{ ...inp, width: 44 }} /></td>
        <td>
          <select value={form.glass_shape} onChange={e => set('glass_shape', e.target.value)} style={inp}>
            <option value="RECT">RECT</option><option value="SHAPED">SHAPED</option><option value="NOTCHED">NOTCHED</option>
          </select>
        </td>
        <td>
          <select value={form.pattern_type} onChange={e => set('pattern_type', e.target.value)} style={inp}>
            <option value="SQ">SQ</option><option value="DIA">DIA</option><option value="NONE">NONE</option>
          </select>
        </td>
        <td>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <input type="number" value={form.grids_across} onChange={e => set('grids_across', e.target.value)} style={{ ...inp, width: 38 }} />
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>×</span>
            <input type="number" value={form.grids_down} onChange={e => set('grids_down', e.target.value)} style={{ ...inp, width: 38 }} />
          </div>
        </td>
        <td style={{ textAlign: 'center' }}><input type="checkbox" checked={form.is_frosted} onChange={e => set('is_frosted', e.target.checked)} /></td>
        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>{sf || '—'}</td>
        <td><input type="number" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} placeholder="0.00" style={{ ...inp, width: 70 }} /></td>
        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>
          {form.unit_price !== '' && form.unit_price != null && form.quantity ? formatCurrency(parseFloat(form.unit_price) * parseInt(form.quantity)) : '—'}
        </td>
        <td colSpan={2}></td>
      </tr>
      <tr style={{ background: 'rgba(255,160,0,0.04)' }}>
        <td colSpan={11} style={{ paddingBottom: 8 }}>
          <input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Notes (optional)..." style={{ ...inp, width: '100%' }} />
        </td>
        <td style={{ paddingBottom: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={add} disabled={saving}>{saving ? '...' : '+ Add'}</button>
        </td>
      </tr>
    </>
  );
}

// ============================================================
// Edit Line Item Row
// ============================================================
function EditLineRow({ li, quoteId, onSaved, onCancel }) {
  const [form, setForm] = useState({
    location_description: li.location_description || '',
    width_inches: li.width_inches != null ? Number(li.width_inches) : null,
    height_inches: li.height_inches != null ? Number(li.height_inches) : null,
    quantity: li.quantity || 1, glass_shape: li.glass_shape || 'RECT',
    pattern_type: li.pattern_type || 'SQ', grids_across: li.grids_across || 0,
    grids_down: li.grids_down || 0, is_frosted: li.is_frosted || false,
    unit_price: li.unit_price != null ? String(li.unit_price) : '',
    unit_cost: li.unit_cost != null ? String(li.unit_cost) : '',
    notes: li.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const sf = sqFt(form.width_inches, form.height_inches);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/quotes/${quoteId}/lines/${li.id}`, {
        location_description: form.location_description, description: form.location_description,
        width_inches: form.width_inches != null ? Number(form.width_inches) : null,
        height_inches: form.height_inches != null ? Number(form.height_inches) : null,
        quantity: parseInt(form.quantity) || 1, glass_shape: form.glass_shape,
        pattern_type: form.pattern_type, grids_across: parseInt(form.grids_across) || 0,
        grids_down: parseInt(form.grids_down) || 0, is_frosted: form.is_frosted,
        unit_price: form.unit_price !== '' ? parseFloat(form.unit_price) : null,
        unit_cost: form.unit_cost !== '' ? parseFloat(form.unit_cost) : null,
        notes: form.notes || null,
      });
      onSaved();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const inp = { fontSize: 12, padding: '3px 6px' };
  return (
    <>
      <tr style={{ background: 'rgba(240,165,0,0.07)' }}>
        <td><input value={form.location_description} onChange={e => set('location_description', e.target.value)} style={{ ...inp, width: '100%', minWidth: 110 }} /></td>
        <td>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <FractionInput value={form.width_inches} onChange={v => set('width_inches', v)} placeholder='W"' style={{ ...inp, width: 72 }} />
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>×</span>
            <FractionInput value={form.height_inches} onChange={v => set('height_inches', v)} placeholder='H"' style={{ ...inp, width: 72 }} />
          </div>
        </td>
        <td><input type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)} style={{ ...inp, width: 44 }} /></td>
        <td>
          <select value={form.glass_shape} onChange={e => set('glass_shape', e.target.value)} style={inp}>
            <option value="RECT">RECT</option><option value="SHAPED">SHAPED</option><option value="NOTCHED">NOTCHED</option>
          </select>
        </td>
        <td>
          <select value={form.pattern_type} onChange={e => set('pattern_type', e.target.value)} style={inp}>
            <option value="SQ">SQ</option><option value="DIA">DIA</option><option value="NONE">NONE</option>
          </select>
        </td>
        <td>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <input type="number" value={form.grids_across} onChange={e => set('grids_across', e.target.value)} style={{ ...inp, width: 38 }} />
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>×</span>
            <input type="number" value={form.grids_down} onChange={e => set('grids_down', e.target.value)} style={{ ...inp, width: 38 }} />
          </div>
        </td>
        <td style={{ textAlign: 'center' }}><input type="checkbox" checked={form.is_frosted} onChange={e => set('is_frosted', e.target.checked)} /></td>
        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>{sf || '—'}</td>
        <td><input type="number" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} placeholder="0.00" style={{ ...inp, width: 70 }} /></td>
        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>
          {form.unit_price !== '' && form.unit_price != null && form.quantity ? formatCurrency(parseFloat(form.unit_price) * parseInt(form.quantity)) : '—'}
        </td>
        <td colSpan={2}></td>
      </tr>
      <tr style={{ background: 'rgba(240,165,0,0.07)' }}>
        <td colSpan={11} style={{ paddingBottom: 8 }}>
          <input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Notes..." style={{ ...inp, width: '100%' }} />
        </td>
        <td style={{ paddingBottom: 8, display: 'flex', gap: 4 }}>
          <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? '...' : 'Save'}</button>
        </td>
      </tr>
    </>
  );
}

// ============================================================
// Quote Totals Block
// ============================================================
function QuoteTotalsBlock({ lineSubtotal, quoteId, quote, onUpdated }) {
  const [shipping, setShipping] = useState(String(Number(quote.shipping || 0)));
  const [taxRate, setTaxRate] = useState(String(Number(quote.sales_tax_rate || 0)));
  const [taxExempt, setTaxExempt] = useState(Boolean(quote.tax_exempt));

  useEffect(() => { setShipping(String(Number(quote.shipping || 0))); }, [quote.shipping]);
  useEffect(() => { setTaxRate(String(Number(quote.sales_tax_rate || 0))); }, [quote.sales_tax_rate]);
  useEffect(() => { setTaxExempt(Boolean(quote.tax_exempt)); }, [quote.tax_exempt]);

  const save = async (patch) => {
    try { await api.patch(`/quotes/${quoteId}`, patch); onUpdated?.(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const shippingNum = parseFloat(shipping) || 0;
  const taxRateNum = parseFloat(taxRate) || 0;
  const subtotal = lineSubtotal + shippingNum;
  const taxAmount = taxExempt ? 0 : subtotal * (taxRateNum / 100);
  const total = subtotal + taxAmount;

  const fmt = formatCurrency;
  const rowSt = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' };
  const lbl = { fontSize: 13, color: 'var(--text-secondary)' };
  const mono = { fontFamily: 'var(--font-mono)', fontSize: 13 };

  return (
    <div className="card" style={{ maxWidth: 420, marginLeft: 'auto', marginTop: 12 }}>
      <div style={{ padding: '14px 20px' }}>
        <div style={rowSt}>
          <span style={lbl}>Line items subtotal</span>
          <span style={mono}>{fmt(lineSubtotal)}</span>
        </div>
        <div style={rowSt}>
          <span style={lbl}>Shipping</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 13 }}>$</span>
            <input type="number" step="0.01" min="0" value={shipping}
              onChange={e => setShipping(e.target.value)}
              onBlur={() => save({ shipping: parseFloat(shipping) || 0 })}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13, width: 88, textAlign: 'right', padding: '2px 6px' }}
            />
          </div>
        </div>
        <div style={rowSt}>
          <span style={lbl}>Subtotal</span>
          <span style={mono}>{fmt(subtotal)}</span>
        </div>
        <div style={rowSt}>
          <span style={lbl}>Tax exempt</span>
          <select value={taxExempt ? 'yes' : 'no'}
            onChange={e => { const v = e.target.value === 'yes'; setTaxExempt(v); save({ tax_exempt: v }); }}
            style={{ fontSize: 12, padding: '2px 6px' }}>
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
        <div style={rowSt}>
          <span style={{ ...lbl, color: taxExempt ? 'var(--text-muted)' : 'var(--text-secondary)' }}>
            Sales tax
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" step="0.001" min="0" max="100" value={taxRate}
              onChange={e => setTaxRate(e.target.value)}
              onBlur={() => save({ sales_tax_rate: parseFloat(taxRate) || 0 })}
              disabled={taxExempt}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 12, width: 48, textAlign: 'right', padding: '2px 4px', opacity: taxExempt ? 0.4 : 1 }}
            />
            <span style={{ fontSize: 12, color: taxExempt ? 'var(--text-muted)' : 'var(--text-secondary)' }}>%</span>
            <span style={{ ...mono, color: taxExempt ? 'var(--text-muted)' : 'inherit', minWidth: 80, textAlign: 'right' }}>
              {fmt(taxAmount)}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0 0', borderTop: '2px solid var(--border)', marginTop: 2 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>Total</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 17, fontWeight: 700, color: 'var(--accent)' }}>{fmt(total)}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Quote Detail View
// ============================================================
function QuoteDetail({ quoteId, customers, onBack, onConverted }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusSaving, setStatusSaving] = useState(false);
  const [convertSaving, setConvertSaving] = useState(false);
  const [editingHeader, setEditingHeader] = useState(false);
  const [editingLineId, setEditingLineId] = useState(null);
  const [hf, setHf] = useState({});
  const setH = (f, v) => setHf(p => ({ ...p, [f]: v }));

  // Inject print CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'cgs-quote-print-style';
    style.textContent = `
      @media print {
        * { visibility: hidden !important; }
        #quote-print-view, #quote-print-view * { visibility: visible !important; }
        #quote-print-view { position: absolute; left: 0; top: 0; width: 100%; background: #fff; padding: 0; }
        @page { size: A4; margin: 1.5cm; }
      }
    `;
    document.head.appendChild(style);
    return () => document.getElementById('cgs-quote-print-style')?.remove();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/quotes/${quoteId}`);
      const q = res.data;
      setQuote(q);
      setHf({
        quote_number: q.quote_number || '',
        customer_id: q.customer_id ? String(q.customer_id) : '',
        expiry_date: q.expiry_date ? q.expiry_date.slice(0, 10) : '',
        notes: q.notes || '',
      });
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [quoteId]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (newStatus) => {
    setStatusSaving(true);
    try { await api.patch(`/quotes/${quoteId}`, { status: newStatus }); await load(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setStatusSaving(false); }
  };

  const saveHeader = async () => {
    try {
      await api.patch(`/quotes/${quoteId}`, {
        quote_number: hf.quote_number,
        customer_id: hf.customer_id ? parseInt(hf.customer_id) : null,
        expiry_date: hf.expiry_date || null,
        notes: hf.notes,
      });
      setEditingHeader(false);
      await load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const deleteLine = async (lineId) => {
    if (!window.confirm('Remove this line item?')) return;
    try { await api.delete(`/quotes/${quoteId}/lines/${lineId}`); await load(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const convert = async () => {
    if (!window.confirm('Convert this quote to a production order?\n\nThis will set the status to Accepted, create a Project, and create individual Unit records for each line item.')) return;
    setConvertSaving(true);
    try {
      const res = await api.post(`/quotes/${quoteId}/convert`);
      const { project_id, units_created } = res.data;
      alert(`Order created successfully!\n\nProject ID: ${project_id}\nUnits created: ${units_created}\n\nNavigate to Projects to view the new order.`);
      onConverted?.();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setConvertSaving(false); }
  };

  if (loading) return <div className="loading">Loading quote...</div>;
  if (!quote) return null;

  const lines = quote.line_items || [];
  const lineSubtotal = lines.reduce((sum, li) => sum + (Number(li.unit_price || 0) * Number(li.quantity || 1)), 0);
  const customerName = customers.find(c => c.id === quote.customer_id)?.company_name || '';
  const customer = customers.find(c => c.id === quote.customer_id) || null;
  const editable = ['draft', 'sent'].includes(quote.status);

  const statusActions = {
    draft: [{ label: 'Mark as Sent', value: 'sent', cls: 'btn-secondary' }],
    sent: [
      { label: 'Mark as Accepted', value: 'accepted', cls: 'btn-success' },
      { label: 'Mark as Rejected', value: 'rejected', cls: 'btn-danger' },
    ],
    accepted: [], rejected: [], expired: [],
  };

  return (
    <div>
      {/* Title bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← All Quotes</button>
        <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', letterSpacing: 1, color: 'var(--accent)', fontSize: 18 }}>
          {quote.quote_number}
        </h3>
        <span className={`badge badge-${quote.status}`}>{quote.status}</span>
        {customerName && <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{customerName}</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => window.print()}>Print Quote</button>
          {(statusActions[quote.status] || []).map(btn => (
            <button key={btn.value} className={`btn ${btn.cls} btn-sm`} disabled={statusSaving} onClick={() => setStatus(btn.value)}>
              {btn.label}
            </button>
          ))}
          {quote.status === 'accepted' && (
            <button className="btn btn-primary btn-sm" disabled={convertSaving} onClick={convert}>
              {convertSaving ? 'Converting...' : 'Convert to Order →'}
            </button>
          )}
        </div>
      </div>

      {/* Quote header card */}
      <div className="card" style={{ marginBottom: 16 }}>
        {editingHeader ? (
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 10 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Quote Number</label>
                <input value={hf.quote_number} onChange={e => setH('quote_number', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Customer</label>
                <select value={hf.customer_id} onChange={e => setH('customer_id', e.target.value)}>
                  <option value="">Select...</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Expiry Date</label>
                <input type="date" value={hf.expiry_date} onChange={e => setH('expiry_date', e.target.value)} />
              </div>
              <div style={{ margin: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>VALUE (AUTO)</label>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, padding: '6px 0', color: 'var(--text-secondary)' }}>
                  {formatCurrency(lineSubtotal)}
                </div>
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label>Notes</label>
              <textarea value={hf.notes} onChange={e => setH('notes', e.target.value)} rows={2} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingHeader(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveHeader}>Save</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '12px 16px', display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Customer</div>
              <div style={{ fontWeight: 600 }}>{customerName || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Value</div>
              <div style={{ fontFamily: 'var(--font-mono)' }}>
                £{lineSubtotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Valid Until</div>
              <div>{quote.expiry_date ? new Date(quote.expiry_date).toLocaleDateString('en-GB') : '—'}</div>
            </div>
            {quote.notes && (
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Notes</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{quote.notes}</div>
              </div>
            )}
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setEditingHeader(true)}>Edit</button>
          </div>
        )}
      </div>

      {/* IGU Spec */}
      <IguSpecBlock quoteId={quoteId} initial={quote.igu_spec} onUpdated={load} />

      {/* Line Items */}
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 10 }}>
        Line Items
      </div>
      <div className="card">
        {lines.length === 0 && !editable ? (
          <div className="empty-state">No line items</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Location</th><th>W × H (in)</th><th>Qty</th><th>Shape</th>
                  <th>Pattern</th><th>Grids A×D</th><th>Frost</th>
                  <th style={{ textAlign: 'right' }}>Sq Ft</th>
                  <th style={{ textAlign: 'right' }}>Unit $</th>
                  <th style={{ textAlign: 'right' }}>Line $</th>
                  <th>Notes</th><th></th>
                </tr>
              </thead>
              <tbody>
                {lines.map(li => {
                  if (editingLineId === li.id) {
                    return (
                      <EditLineRow key={li.id} li={li} quoteId={quoteId}
                        onSaved={() => { setEditingLineId(null); load(); }}
                        onCancel={() => setEditingLineId(null)} />
                    );
                  }
                  return (
                    <tr key={li.id}>
                      <td style={{ fontSize: 12 }}>{li.location_description || li.description || '—'}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                        {li.width_inches && li.height_inches ? `${formatFraction(li.width_inches)} × ${formatFraction(li.height_inches)}` : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>{li.quantity || 1}</td>
                      <td style={{ fontSize: 12 }}>{li.glass_shape || '—'}</td>
                      <td style={{ fontSize: 12 }}>{li.pattern_type || '—'}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                        {(li.grids_across || li.grids_down) ? `${li.grids_across || 0}×${li.grids_down || 0}` : '—'}
                      </td>
                      <td style={{ textAlign: 'center', color: 'var(--accent)' }}>{li.is_frosted ? '✓' : ''}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>
                        {li.sq_ft_actual ? Number(li.sq_ft_actual).toFixed(3) : (li.width_inches && li.height_inches ? sqFt(li.width_inches, li.height_inches) : '—')}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'right' }}>
                        {li.unit_price != null ? formatCurrency(Number(li.unit_price)) : '—'}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, textAlign: 'right' }}>
                        {li.unit_price != null ? formatCurrency(Number(li.unit_price) * Number(li.quantity || 1)) : '—'}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden' }}>
                        {li.notes ? <span title={li.notes}>{li.notes.length > 28 ? li.notes.slice(0, 28) + '…' : li.notes}</span> : null}
                      </td>
                      <td style={{ display: 'flex', gap: 4 }}>
                        {editable && <>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditingLineId(li.id)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteLine(li.id)}>×</button>
                        </>}
                      </td>
                    </tr>
                  );
                })}
                {editable && <AddLineRow quoteId={quoteId} onAdded={load} />}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Totals */}
      <QuoteTotalsBlock lineSubtotal={lineSubtotal} quoteId={quoteId} quote={quote} onUpdated={load} />

      {/* Print view — hidden on screen, shown only when printing */}
      <QuotePrint quote={quote} customer={customer} lineSubtotal={lineSubtotal} />
    </div>
  );
}

// ============================================================
// Quote Print View
// ============================================================
function QuotePrint({ quote, customer, lineSubtotal }) {
  const spec = quote.igu_spec || {};
  const lines = quote.line_items || [];
  const shipping = Number(quote.shipping || 0);
  const taxRate = Number(quote.sales_tax_rate || 0);
  const taxExempt = Boolean(quote.tax_exempt);
  const subtotal = lineSubtotal + shipping;
  const taxAmount = taxExempt ? 0 : subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;

  const fmt = formatCurrency;
  const quoteDate = quote.issue_date
    ? new Date(quote.issue_date).toLocaleDateString('en-US')
    : new Date().toLocaleDateString('en-US');
  const expiryDate = quote.expiry_date
    ? new Date(quote.expiry_date).toLocaleDateString('en-US')
    : '—';

  const specRows = [
    [['O/A Size', spec.oa_size], ['Outboard Lite', spec.outboard_lite], ['Outboard Thickness', spec.outboard_thickness]],
    [['Air Space', spec.air_space], ['Air Type', spec.air_type], ['Inboard Lite', spec.inboard_lite]],
    [['Inboard Thickness', spec.inboard_thickness], ['Low-E', spec.low_e], ['Low-E Type', spec.low_e === 'yes' ? spec.low_e_type : '—']],
    [['Low-E Surface', spec.low_e === 'yes' ? spec.low_e_surface : '—'], ['Primary Seal', spec.primary_seal], ['Secondary Seal', spec.secondary_seal]],
    [['Edge Bite', spec.edge_bite], ['Lead Type', spec.lead_type], ['Lead Sides', spec.lead_sides]],
    [['Leaded Units', spec.leaded_units], ['Perimeter Lead', spec.perimeter_lead], ["Soldered C/O's", spec.soldered_cos]],
    [['Logos', spec.logos], ['Grids in Units', spec.grids_in_units], ['Muntin Colour', spec.grids_in_units === 'yes' ? spec.muntin_colour : '—']],
    [['Muntin Size', spec.grids_in_units === 'yes' ? spec.muntin_size : '—'], ['Spacer Bar Colour', spec.spacer_bar_colour], ['Spacer Bar Size', spec.spacer_bar_size]],
    [['Surface Finish', spec.surface_finish], ['Warranty', spec.warranty], ['', '']],
  ];

  const cellSt = { border: '1px solid #ccc', padding: '3px 7px', width: '33.3%', fontSize: 10 };

  return (
    <div id="quote-print-view" style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 11, color: '#000', padding: 0, background: '#fff' }}>

      {/* Header */}
      <table style={{ width: '100%', marginBottom: 14, borderCollapse: 'collapse' }}>
        <tbody><tr>
          <td style={{ verticalAlign: 'top' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>CREATIVE GLASS SYSTEMS INC.</div>
            <div style={{ fontSize: 10 }}>901 William Leigh Drive, Bristol PA 19007</div>
            <div style={{ fontSize: 10 }}>Tel: 267-202-6553 &nbsp;&nbsp; Fax: 855-260-9393</div>
            <div style={{ fontSize: 10 }}>sales@creativeglasssystems.com &nbsp; www.creativeglasssystems.com</div>
          </td>
          <td style={{ textAlign: 'right', verticalAlign: 'top' }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>QUOTATION</div>
            <table style={{ marginLeft: 'auto', borderCollapse: 'collapse', fontSize: 11 }}>
              <tbody>
                <tr><td style={{ paddingRight: 12, color: '#555' }}>Quote #:</td><td style={{ fontWeight: 600 }}>{quote.quote_number}</td></tr>
                <tr><td style={{ color: '#555' }}>Date:</td><td>{quoteDate}</td></tr>
                <tr><td style={{ color: '#555' }}>Valid Until:</td><td>{expiryDate}</td></tr>
              </tbody>
            </table>
          </td>
        </tr></tbody>
      </table>
      <hr style={{ border: 'none', borderTop: '2px solid #000', marginBottom: 12 }} />

      {/* Bill To */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 4, textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: 3 }}>Bill To</div>
        {customer && (
          <div style={{ fontSize: 11, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600 }}>{customer.company_name}</div>
            {customer.contact_name && <div>{customer.contact_name}</div>}
            {customer.address && <div>{customer.address}</div>}
            {customer.address_2 && <div>{customer.address_2}</div>}
            {(customer.city || customer.state) && <div>{[customer.city, customer.state].filter(Boolean).join(', ')}</div>}
            {customer.phone && <div>Tel: {customer.phone}</div>}
            {customer.email && <div>{customer.email}</div>}
          </div>
        )}
        {quote.notes && <div style={{ marginTop: 6, fontSize: 10, color: '#555' }}><strong>Notes:</strong> {quote.notes}</div>}
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #ccc', marginBottom: 12 }} />

      {/* Description of Work */}
      <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6, textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: 3 }}>Description of Work</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
        <tbody>
          {specRows.map((row, ri) => (
            <tr key={ri}>
              {row.map(([label, val], ci) => (
                <td key={ci} style={cellSt}>
                  {label ? <><span style={{ color: '#888', fontSize: 9 }}>{label}: </span><span style={{ fontWeight: 600 }}>{val || '—'}</span></> : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {spec.notes && <div style={{ fontSize: 10, marginBottom: 10, color: '#444' }}><strong>Spec Notes:</strong> {spec.notes}</div>}

      {/* Line Items */}
      <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6, textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: 3 }}>Line Items</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14, fontSize: 10 }}>
        <thead>
          <tr style={{ background: '#f2f2f2' }}>
            <th style={{ ...cellSt, width: 24, textAlign: 'center', fontWeight: 600 }}>#</th>
            <th style={{ ...cellSt, textAlign: 'left', fontWeight: 600 }}>Location / Description</th>
            <th style={{ ...cellSt, width: 80, textAlign: 'center', fontWeight: 600 }}>Size (W × H in)</th>
            <th style={{ ...cellSt, width: 28, textAlign: 'center', fontWeight: 600 }}>Qty</th>
            <th style={{ ...cellSt, width: 70, textAlign: 'right', fontWeight: 600 }}>Unit Price</th>
            <th style={{ ...cellSt, width: 70, textAlign: 'right', fontWeight: 600 }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((li, i) => (
            <tr key={li.id}>
              <td style={{ ...cellSt, textAlign: 'center' }}>{i + 1}</td>
              <td style={cellSt}>
                <div style={{ fontWeight: 500 }}>{li.location_description || li.description || '—'}</div>
                {li.glass_shape && li.glass_shape !== 'RECT' && <div style={{ fontSize: 9, color: '#666' }}>{li.glass_shape}{li.pattern_type && li.pattern_type !== 'NONE' ? ` / ${li.pattern_type}` : ''}</div>}
                {li.is_frosted && <div style={{ fontSize: 9, color: '#666' }}>Frosted</div>}
                {li.notes && <div style={{ fontSize: 9, color: '#666', fontStyle: 'italic' }}>{li.notes}</div>}
              </td>
              <td style={{ ...cellSt, textAlign: 'center' }}>
                {li.width_inches && li.height_inches ? `${formatFraction(li.width_inches)} × ${formatFraction(li.height_inches)}` : '—'}
              </td>
              <td style={{ ...cellSt, textAlign: 'center' }}>{li.quantity || 1}</td>
              <td style={{ ...cellSt, textAlign: 'right' }}>{li.unit_price ? fmt(li.unit_price) : '—'}</td>
              <td style={{ ...cellSt, textAlign: 'right', fontWeight: 600 }}>
                {li.unit_price ? fmt(Number(li.unit_price) * Number(li.quantity || 1)) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <table style={{ width: '38%', minWidth: 260, marginLeft: 'auto', borderCollapse: 'collapse', marginBottom: 16, fontSize: 11 }}>
        <tbody>
          <tr>
            <td style={{ padding: '3px 10px' }}>Line Items Subtotal</td>
            <td style={{ padding: '3px 10px', textAlign: 'right' }}>{fmt(lineSubtotal)}</td>
          </tr>
          <tr>
            <td style={{ padding: '3px 10px', color: '#777', fontSize: 10 }}>Energy Surcharge (1.5% incl.)</td>
            <td style={{ padding: '3px 10px', textAlign: 'right', color: '#777', fontSize: 10 }}>Included</td>
          </tr>
          {shipping > 0 && (
            <tr>
              <td style={{ padding: '3px 10px' }}>Shipping</td>
              <td style={{ padding: '3px 10px', textAlign: 'right' }}>{fmt(shipping)}</td>
            </tr>
          )}
          {taxExempt ? (
            <tr>
              <td style={{ padding: '3px 10px', color: '#777' }}>Sales Tax</td>
              <td style={{ padding: '3px 10px', textAlign: 'right', color: '#777' }}>Exempt</td>
            </tr>
          ) : taxRate > 0 ? (
            <tr>
              <td style={{ padding: '3px 10px' }}>Sales Tax ({taxRate}%)</td>
              <td style={{ padding: '3px 10px', textAlign: 'right' }}>{fmt(taxAmount)}</td>
            </tr>
          ) : null}
          <tr style={{ borderTop: '2px solid #000' }}>
            <td style={{ padding: '6px 10px', fontWeight: 700, fontSize: 13 }}>TOTAL</td>
            <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(total)}</td>
          </tr>
        </tbody>
      </table>

      {/* Terms */}
      <hr style={{ border: 'none', borderTop: '1px solid #ccc', marginBottom: 8 }} />
      <div style={{ fontWeight: 700, fontSize: 10, marginBottom: 4, textTransform: 'uppercase' }}>Terms &amp; Conditions</div>
      <div style={{ fontSize: 9, color: '#444', lineHeight: 1.5, columns: 2, columnGap: 20 }}>
        <p style={{ margin: '0 0 4px' }}>1. This quotation is valid for 30 days from the date of issue unless otherwise stated.</p>
        <p style={{ margin: '0 0 4px' }}>2. All prices are in USD. Payment terms: 50% deposit required to commence production, balance due prior to delivery.</p>
        <p style={{ margin: '0 0 4px' }}>3. All glass units are manufactured to the specifications noted above. Field measurements are the responsibility of the contractor or owner.</p>
        <p style={{ margin: '0 0 4px' }}>4. Creative Glass Systems Inc. warrants all insulated glass units against seal failure and moisture ingress for the warranty period stated above under normal use conditions.</p>
        <p style={{ margin: '0 0 4px' }}>5. Cancellations after production has commenced will be subject to a restocking charge. Custom units cannot be returned.</p>
        <p style={{ margin: '0 0 4px' }}>6. Delivery dates are estimates only and subject to change due to production schedules and material availability.</p>
        <p style={{ margin: '0 0 4px' }}>7. This quotation constitutes an offer to sell and becomes a binding contract upon receipt of deposit or written acceptance.</p>
      </div>
    </div>
  );
}

// ============================================================
// Main Sales Component
// ============================================================
export default function Sales() {
  const [tab, setTab] = useState('Quotes');
  const [quotes, setQuotes] = useState([]);
  const [opps, setOpps] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewingQuoteId, setViewingQuoteId] = useState(null);
  const [showNewQuote, setShowNewQuote] = useState(false);
  const [showOppModal, setShowOppModal] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/quotes'), api.get('/opportunities'), api.get('/customers')])
      .then(([q, o, c]) => {
        setQuotes(q.data.value || []);
        setOpps(o.data.value || []);
        setCustomers(c.data.value || []);
        setLoading(false);
      }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDeleteQuote = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this quote? This cannot be undone.')) return;
    try { await api.delete(`/quotes/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const handleDeleteOpp = async (id) => {
    if (!window.confirm('Delete this inquiry? This cannot be undone.')) return;
    try { await api.delete(`/opportunities/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const filteredQuotes = quotes.filter(q =>
    !search || q.quote_number?.toLowerCase().includes(search.toLowerCase()) || q.customer_name?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredOpps = opps.filter(o =>
    !search || o.customer_name?.toLowerCase().includes(search.toLowerCase()) || o.title?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPipeline = filteredOpps.filter(o => !['won', 'lost'].includes(o.stage)).reduce((sum, o) => sum + Number(o.estimated_value || 0), 0);

  const tabBar = (activeTab) => (
    <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
      {TABS.map(t => (
        <button key={t} onClick={() => { setViewingQuoteId(null); setTab(t); }} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '10px 24px',
          fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
          color: t === activeTab ? 'var(--accent)' : 'var(--text-muted)',
          borderBottom: t === activeTab ? '2px solid var(--accent)' : '2px solid transparent',
          marginBottom: -1, transition: 'all 0.15s'
        }}>{t}</button>
      ))}
    </div>
  );

  if (viewingQuoteId) {
    return (
      <div>
        <div className="page-header"><h2 className="page-title">SA<span>LES</span></h2></div>
        {tabBar('Quotes')}
        <QuoteDetail
          quoteId={viewingQuoteId} customers={customers}
          onBack={() => { setViewingQuoteId(null); load(); }}
          onConverted={() => { setViewingQuoteId(null); load(); }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">SA<span>LES</span></h2>
        <button className="btn btn-primary" onClick={() => tab === 'Quotes' ? setShowNewQuote(true) : setShowOppModal(true)}>
          + New {tab === 'Quotes' ? 'Quote' : 'Inquiry'}
        </button>
      </div>

      {tabBar(tab)}

      <div className="search-bar">
        <input
          placeholder={tab === 'Quotes' ? 'Search by quote number or customer...' : 'Search by customer or title...'}
          value={search} onChange={e => setSearch(e.target.value)}
        />
        {tab === 'Inquiries' && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            Active Inquiries: {formatCurrency(totalPipeline)}
          </span>
        )}
      </div>

      <div className="card">
        {loading ? <div className="loading">Loading...</div> : (
          tab === 'Quotes' ? (
            filteredQuotes.length === 0 ? <div className="empty-state">No quotes found</div> : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Quote #</th><th>Customer</th><th>Status</th><th>Total</th><th>Valid Until</th><th>Created</th><th></th></tr>
                  </thead>
                  <tbody>
                    {filteredQuotes.map(q => (
                      <tr key={q.id} style={{ cursor: 'pointer' }} onClick={() => setViewingQuoteId(q.id)}>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>
                          {q.quote_number || `Q-${String(q.id).padStart(4, '0')}`}
                        </td>
                        <td style={{ fontWeight: 600 }}>{q.customer_name || '—'}</td>
                        <td><span className={`badge badge-${q.status}`}>{q.status}</span></td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>
                          {q.value != null ? formatCurrency(Number(q.value)) : '—'}
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                          {q.expiry_date ? new Date(q.expiry_date).toLocaleDateString('en-GB') : '—'}
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                          {q.created_at ? new Date(q.created_at).toLocaleDateString('en-GB') : '—'}
                        </td>
                        <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => setViewingQuoteId(q.id)}>View</button>
                          <button className="btn btn-danger btn-sm" onClick={e => handleDeleteQuote(e, q.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            filteredOpps.length === 0 ? <div className="empty-state">No inquiries found</div> : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Title</th><th>Customer</th><th>Stage</th><th>Est. Value</th><th>Expected Close</th><th></th></tr>
                  </thead>
                  <tbody>
                    {filteredOpps.map(o => (
                      <tr key={o.id}>
                        <td style={{ fontWeight: 600 }}>{o.title}</td>
                        <td style={{ color: 'var(--text-secondary)' }}>{o.customer_name || '—'}</td>
                        <td><span className={`badge badge-${o.stage}`}>{INQUIRY_STAGES.find(s => s.value === o.stage)?.label || o.stage}</span></td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>
                          {o.estimated_value != null ? formatCurrency(Number(o.estimated_value)) : '—'}
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                          {o.expected_close ? new Date(o.expected_close).toLocaleDateString('en-GB') : '—'}
                        </td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditingOpp(o)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeleteOpp(o.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )
        )}
      </div>

      {showNewQuote && (
        <NewQuoteModal customers={customers} onClose={() => setShowNewQuote(false)}
          onSaved={(id) => { setShowNewQuote(false); setViewingQuoteId(id); }} />
      )}
      {showOppModal && (
        <OppModal customers={customers} onClose={() => setShowOppModal(false)} onSaved={() => { setShowOppModal(false); load(); }} />
      )}
      {editingOpp && (
        <OppModal opp={editingOpp} customers={customers} onClose={() => setEditingOpp(null)} onSaved={() => { setEditingOpp(null); load(); }} />
      )}
    </div>
  );
}
